// @ts-nocheck — the study engine (docs/bim-plan/37-window-details.md), ported as-is from
// frontend/window-details-test.html so the two stay line-for-line comparable while the drawings
// are being refined. Typed at its boundary by ./spec.ts; keep changes in BOTH places until the
// test page is rebuilt on this module.
/**
 * PARAMETRIC WINDOW DETAILS — the drawing engine. One set of settings (a WindowDetailSpec, in
 * INCHES) places the window in the wall once (`place`) and draws the head, jamb and sill from
 * that placement, so the three can never disagree. Output: AnnoPrims in FEET (y up) plus the
 * bbox they occupy — derived every time, never stored (the annotation doctrine).
 *
 * Drafting rules Matt set while reviewing (bim-plan/37): rectangular bucks; every lap SHEDS
 * (the layer stack `p.L`); AlderCAD's batt symbol (battLoopsLocal — a copy of bim-geometry's,
 * keep identical); plywood = quarter ply lines + diagonal groups every 4"; notes left-aligned
 * both sides; break lines trim what they cut; lines seen beyond start at their piece.
 */
/* ---------- format ---------- */
function gcd(a,b){return b?gcd(b,a%b):a}
function fmt(inch){var s=Math.round(Math.abs(inch)*16)/16,ft=Math.floor(s/12+1e-9),r=s-ft*12,w=Math.floor(r+1e-9),f=Math.round((r-w)*16);if(f===16){w++;f=0}
  var fr=f?(f/gcd(f,16))+'/'+(16/gcd(f,16)):'';
  if(ft)return ft+"'-"+w+(fr?' '+fr:'')+'"';return (w?w+(fr?' ':''):'')+(fr||(w?'':'0'))+'"'}

/* ---------- the drawing surface: world INCHES, y up; emits AnnoPrims in FEET ----------
   The study page drew SVG with CSS classes; here each class is a pen (w = stroke-width
   multiplier, as AnnoPrims takes it), an optional ink color, a solid fill color and/or a
   generated fill pattern (strokes and dots clipped to the shape). */
var INK = '#1c2629'
var STYLE = {
  cut: { w: 1.6 }, med: { w: 1.0 }, fine: { w: 0.5 }, lead: { w: 0.5 },
  beyond: { w: 0.45, color: '#7b8683' }, hid: { w: 0.6, dash: true },
  woodcut: { w: 1.6 }, woodplain: { w: 1.1 }, ply: { w: 1.1 }, void: { w: 0.5 },
  woodf: { w: 1.1, pat: 'grain' }, plaster: { w: 1.2, pat: 'stip' }, gyp: { w: 1.1, pat: 'gyp' },
  cmu: { w: 1.5, fill: '#d6d8d4', pat: 'cross' }, grout: { w: 1.5, fill: '#eceae4', pat: 'grout' },
  clad: { w: 0.5, fill: '#3b4346' }, alum: { w: 0.9, fill: '#c9d0d2' }, fgf: { w: 0.9, fill: '#dfe3d6' },
  steelf: { w: 0.9, fill: '#4a5357' }, glass: { w: 0.7, fill: '#dbe7ea' }, stone: { w: 1.6, fill: '#a9aea9' },
  tbk: { fill: INK, none: true }, dark: { fill: INK, none: true }, fin: { fill: INK, none: true },
  seal: { fill: INK, none: true }, dot: { fill: INK, none: true }, rod: { w: 0.7 },
  memb: { w: 2.4, color: '#e01ab5' }, flash: { w: 1.5 }, copper: { w: 1.9, color: '#a85a2e' },
  wrb: { w: 0.6, dash: true }, cladln: { w: 2.2, color: '#3b4346' },
  conc: { w: 1.5, pat: 'conc' }, joint: { w: 0.6, pat: 'cross' }, floorln: { w: 0.6, dash: true },
}
var F12 = function (p) { return [p[0] / 12, p[1] / 12] }

/* ---- small 2D helpers (the kernel can't import bim-geometry's clip2d) ---- */
function inPoly(x, y, ring) {
  var c = false
  for (var i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    var a = ring[i], b = ring[j]
    if ((a[1] > y) !== (b[1] > y) && x < ((b[0] - a[0]) * (y - a[1])) / (b[1] - a[1]) + a[0]) c = !c
  }
  return c
}
/** the inside pieces of a polyline against any (even non-convex) ring */
function clipLine(pts, ring) {
  var out = [], cur = null
  for (var i = 0; i + 1 < pts.length; i++) {
    var p = pts[i], q = pts[i + 1], ts = [0, 1]
    for (var k = 0, m = ring.length - 1; k < ring.length; m = k++) {
      var a = ring[m], b = ring[k]
      var rx = q[0] - p[0], ry = q[1] - p[1], sx = b[0] - a[0], sy = b[1] - a[1]
      var den = rx * sy - ry * sx
      if (Math.abs(den) < 1e-12) continue
      var t = ((a[0] - p[0]) * sy - (a[1] - p[1]) * sx) / den, u = ((a[0] - p[0]) * ry - (a[1] - p[1]) * rx) / den
      if (t > 0 && t < 1 && u >= 0 && u <= 1) ts.push(t)
    }
    ts.sort(function (x, y) { return x - y })
    for (var n = 0; n + 1 < ts.length; n++) {
      var t0 = ts[n], t1 = ts[n + 1]
      if (t1 - t0 < 1e-9) continue
      var mid = [p[0] + (q[0] - p[0]) * (t0 + t1) / 2, p[1] + (q[1] - p[1]) * (t0 + t1) / 2]
      var A = [p[0] + (q[0] - p[0]) * t0, p[1] + (q[1] - p[1]) * t0], B = [p[0] + (q[0] - p[0]) * t1, p[1] + (q[1] - p[1]) * t1]
      if (inPoly(mid[0], mid[1], ring)) {
        if (cur && Math.hypot(cur[cur.length - 1][0] - A[0], cur[cur.length - 1][1] - A[1]) < 1e-9) cur.push(B)
        else { cur = [A, B]; out.push(cur) }
      } else cur = null
    }
  }
  return out
}
/** a shape clipped to a CONVEX ring (Sutherland–Hodgman); the engine's inner clips are rectangles */
function clipConvex(pts, ring) {
  var out = pts
  for (var i = 0; i < ring.length && out.length; i++) {
    var a = ring[i], b = ring[(i + 1) % ring.length], inp = out
    out = []
    var side = function (p) { return (b[0] - a[0]) * (p[1] - a[1]) - (b[1] - a[1]) * (p[0] - a[0]) }
    var area = 0
    for (var z = 0; z < ring.length; z++) { var r1 = ring[z], r2 = ring[(z + 1) % ring.length]; area += r1[0] * r2[1] - r2[0] * r1[1] }
    var sg = area >= 0 ? 1 : -1
    for (var j = 0; j < inp.length; j++) {
      var P = inp[j], Q = inp[(j + 1) % inp.length], sp = side(P) * sg, sq = side(Q) * sg
      if (sp >= 0) out.push(P)
      if ((sp >= 0) !== (sq >= 0)) { var t = sp / (sp - sq); out.push([P[0] + (Q[0] - P[0]) * t, P[1] + (Q[1] - P[1]) * t]) }
    }
  }
  return out
}
function bboxOf(pts) {
  var b = [1e9, 1e9, -1e9, -1e9]
  pts.forEach(function (p) { b[0] = Math.min(b[0], p[0]); b[1] = Math.min(b[1], p[1]); b[2] = Math.max(b[2], p[0]); b[3] = Math.max(b[3], p[1]) })
  return b
}

function Sheet() { this.o = []; this.notes = []; this.band = null; this.nContent = 0 }
/* prims are held in INCHES while drawing (so clips work in the engine's units) and
   converted to feet once, in result() */
Sheet.prototype.fillPattern = function (pts, pat) {
  var b = bboxOf(pts), self = this
  var strokes = function (lines, color, w) {
    lines.forEach(function (l) { clipLine(l, pts).forEach(function (s) { self.o.push({ kind: 'poly', closed: false, pts: s, w: w, color: color }) }) })
  }
  var dots = function (tile, list, color) {
    for (var x = Math.floor(b[0] / tile) * tile; x < b[2]; x += tile) for (var y = Math.floor(b[1] / tile) * tile; y < b[3]; y += tile) {
      list.forEach(function (d) { var cx = x + d[0], cy = y + d[1]; if (inPoly(cx, cy, pts)) self.o.push({ kind: 'circle', c: [cx, cy], r: d[2], filled: true, color: color }) })
    }
  }
  if (pat === 'grain') {
    var ls = []
    for (var gy = Math.floor(b[1] / 0.22) * 0.22 + 0.11; gy < b[3]; gy += 0.22) {
      var l = []
      for (var gx = b[0] - 0.1; gx <= b[2] + 0.1; gx += 0.1) l.push([gx, gy + 0.06 * Math.sin((2 * Math.PI * gx) / 1.2)])
      ls.push(l)
    }
    strokes(ls, '#8d9794', 0.25)
  } else if (pat === 'stip') dots(0.55, [[0.12, 0.41, 0.03], [0.38, 0.22, 0.025], [0.2, 0.1, 0.02], [0.47, 0.47, 0.018]], '#55615f')
  else if (pat === 'gyp') dots(0.3, [[0.08, 0.2, 0.016], [0.22, 0.06, 0.014]], '#7b8683')
  else if (pat === 'cross') {
    var cl = [], span = (b[2] - b[0]) + (b[3] - b[1])
    for (var k = -span; k < span; k += 0.4) { cl.push([[b[0] + k, b[1]], [b[0] + k + span, b[1] + span]]); cl.push([[b[0] + k, b[3]], [b[0] + k + span, b[3] - span]]) }
    strokes(cl, '#55615f', 0.3)
  } else if (pat === 'conc') dots(1.6, [[0.2, 0.3, 0.035], [0.9, 1.1, 0.045], [1.3, 0.5, 0.03], [0.55, 1.4, 0.025]], '#7b8683')
  else if (pat === 'grout') dots(0.7, [[0.15, 0.5, 0.035], [0.5, 0.25, 0.05], [0.43, 0.6, 0.03]], '#7b8683')
}
Sheet.prototype.poly = function (p, c) {
  var st = STYLE[c] || { w: 1 }
  if (st.pat) {
    this.o.push({ kind: 'poly', closed: true, pts: p, filled: !!st.fill, fillColor: st.fill, fillOpacity: st.fill ? 1 : undefined, w: 0.01, opacity: 0 })
    this.fillPattern(p, st.pat)
    this.o.push({ kind: 'poly', closed: false, pts: p.concat([p[0]]), w: st.w })
  } else if (st.fill) {
    this.o.push(st.none ? { kind: 'poly', closed: true, pts: p, filled: true, fillColor: st.fill, fillOpacity: 1, w: 0.01, opacity: 0 }
      : { kind: 'poly', closed: true, pts: p, filled: true, fillColor: st.fill, fillOpacity: 1, w: st.w })
  } else this.o.push({ kind: 'poly', closed: true, pts: p, w: st.w })
  return this
}
Sheet.prototype.pl = function (p, c) {
  var st = STYLE[c] || { w: 1 }, pr = { kind: 'poly', closed: false, pts: p, w: st.w }
  if (st.color) pr.color = st.color
  if (st.dash) pr.dash = true
  this.o.push(pr)
  return this
}
Sheet.prototype.line = function (a, b, c, d, k) { return this.pl([[a, b], [c, d]], k) }
Sheet.prototype.rect = function (x, y, w, h, c) { if (w < 0) { x += w; w = -w } if (h < 0) { y += h; h = -h } return this.poly([[x, y], [x + w, y], [x + w, y + h], [x, y + h]], c) }
Sheet.prototype.circ = function (x, y, r, c) {
  var st = STYLE[c] || {}
  this.o.push({ kind: 'circle', c: [x, y], r: r, filled: !!st.fill, ...(st.fill ? { color: st.fill } : {}) })
}
Sheet.prototype.text = function (x, y, s, o) {
  o = o || {}
  this.o.push({ kind: 'text', at: [x, y], size: o.size || 0.4, text: String(s), anchor: o.anchor || 'start' })
}
/* draw `fn` clipped to a convex ring (the engine's rectangle clips: lap courses, batt) */
Sheet.prototype.clip = function (ring, fn) {
  var save = this.o
  this.o = []
  fn(this)
  var inner = this.o
  this.o = save
  for (var i = 0; i < inner.length; i++) {
    var pr = inner[i]
    if (pr.kind === 'circle') { if (inPoly(pr.c[0], pr.c[1], ring)) this.o.push(pr) }
    else if (pr.kind === 'text') this.o.push(pr)
    else if (pr.kind === 'poly' && pr.closed) { var cp = clipConvex(pr.pts, ring); if (cp.length >= 3) this.o.push(Object.assign({}, pr, { pts: cp })) }
    else if (pr.kind === 'poly') clipLine(pr.pts, ring).forEach(function (s) { this.o.push(Object.assign({}, pr, { pts: s })) }, this)
  }
}
/* trim everything drawn so far to the band between the two break lines: everything is drawn
   past the breaks, then paper-colored masks cover what lies beyond each zigzag (the drafting
   masking-region convention), so the drawing ends exactly on the zigzag on every surface */
Sheet.prototype.wrapClip = function (region) {
  var top = region.slice(0, 8), bot = region.slice(8)
  var pts = []
  this.o.forEach(function (pr) { if (pr.pts) pts.push.apply(pts, pr.pts); else if (pr.c) pts.push(pr.c) })
  var b = bboxOf(pts), x0 = b[0] - 0.5, x1 = b[2] + 0.5
  var clampX = function (p) { return [Math.max(x0, Math.min(x1, p[0])), p[1]] }
  var ty = top[0][1], by = bot[0][1]
  var tm = top.map(clampX).concat([[x1, b[3] + 0.5], [x0, b[3] + 0.5]])
  var bm = bot.map(clampX).concat([[x0, b[1] - 0.5], [x1, b[1] - 0.5]])
  this.nContent = this.o.length
  this.band = [ty + 1.0, by - 1.0]
  this.o.push({ kind: 'poly', closed: true, pts: tm, w: 0.01, opacity: 0, fillOpacity: 1, mask: true })
  this.o.push({ kind: 'poly', closed: true, pts: bm, w: 0.01, opacity: 0, fillOpacity: 1, mask: true })
}
Sheet.prototype.xbox = function (x, y, w, h, c) { this.rect(x, y, w, h, c || 'woodcut'); this.line(x, y, x + w, y + h, 'fine'); this.line(x, y + h, x + w, y, 'fine') }
Sheet.prototype.note = function (x, y, t, side) { this.notes.push({ x: x, y: y, t: t, side: side }) }
Sheet.prototype.arrowAt = function (x, y, fx, fy) {
  var dx = x - fx, dy = y - fy, L = Math.hypot(dx, dy) || 1; dx /= L; dy /= L; var a = 0.32, w = 0.09
  this.poly([[x, y], [x - dx * a - dy * w, y - dy * a + dx * w], [x - dx * a + dy * w, y - dy * a - dx * w]], 'dot')
}
/** the finished drawing: AnnoPrims in FEET and the bbox they occupy (masks and the content
 *  beyond the breaks excluded) */
Sheet.prototype.svg = function () {
  var bb = [1e9, 1e9, -1e9, -1e9], band = this.band, nC = this.nContent
  var grow = function (x, y) { bb[0] = Math.min(bb[0], x); bb[1] = Math.min(bb[1], y); bb[2] = Math.max(bb[2], x); bb[3] = Math.max(bb[3], y) }
  var prims = this.o.map(function (pr, i) {
    var inBand = band && i < nC
    var g = function (x, y) { if (pr.mask) return; grow(x, inBand ? Math.min(band[0], Math.max(band[1], y)) : y) }
    if (pr.kind === 'text') {
      var wIn = pr.text.length * pr.size * 0.6, x0 = pr.anchor === 'end' ? pr.at[0] - wIn : pr.anchor === 'middle' ? pr.at[0] - wIn / 2 : pr.at[0]
      g(x0, pr.at[1] - pr.size * 0.25); g(x0 + wIn, pr.at[1] + pr.size)
      return { kind: 'text', at: F12(pr.at), hFt: pr.size / 12, text: pr.text, anchor: pr.anchor }
    }
    if (pr.kind === 'circle') { g(pr.c[0] - pr.r, pr.c[1] - pr.r); g(pr.c[0] + pr.r, pr.c[1] + pr.r); return Object.assign({}, pr, { c: F12(pr.c), r: pr.r / 12 }) }
    pr.pts.forEach(function (p) { g(p[0], p[1]) })
    var out = Object.assign({}, pr, { pts: pr.pts.map(F12) })
    delete out.mask
    return out
  })
  return { prims: prims, bboxFt: [bb[0] / 12, bb[1] / 12, bb[2] / 12, bb[3] / 12] }
}

/* notes in two columns, kept in target order, spread only as much as needed */
Sheet.prototype.layoutNotes=function(xL,xR){var self=this,z=0.375,lh=0.47,gap=0.3,wrap=15;
  ['left','right'].forEach(function(side){var xc=side==='left'?xL:xR;
    var ns=self.notes.filter(function(n){return n.side===side}).sort(function(a,b){return b.y-a.y});if(!ns.length)return;
    ns.forEach(function(n){var ws=n.t.split(' '),ls=[],cur='';ws.forEach(function(w){if((cur+' '+w).trim().length>wrap){ls.push(cur.trim());cur=w}else cur+=' '+w});if(cur.trim())ls.push(cur.trim());n.ls=ls;n.h=ls.length*lh;n.top=n.y+lh*0.6});
    for(var i=1;i<ns.length;i++){var lim=ns[i-1].top-ns[i-1].h-gap;if(ns[i].top>lim)ns[i].top=lim}
    for(var j=ns.length-2;j>=0;j--){var need=ns[j+1].top+ns[j].h+gap;if(ns[j].top<need)ns[j].top=need}
    var cw=function(l){return l.length*z*0.6};
    var colW=Math.max.apply(null,ns.map(function(n){return Math.max.apply(null,n.ls.map(cw))}));
    var tx=side==='left'?xc-colW:xc;
    if(self.labels&&self.labels[side])self.text(tx,ns[0].top+0.6,self.labels[side],{size:0.42,anchor:'start'});
    ns.forEach(function(n){var ly=n.top-lh*0.62,kx=side==='left'?tx+cw(n.ls[0])+0.25:xc-0.25,bx=side==='left'?xc+0.6:kx-0.5;
      self.pl([[kx,ly],[bx,ly],[n.x,n.y]],'lead');self.arrowAt(n.x,n.y,bx,ly);
      n.ls.forEach(function(l,i){self.text(tx,n.top-lh*0.8-i*lh,l,{size:z,anchor:'start'})})})})};
Sheet.prototype.dimH=function(x1,x2,y,label,up){var t=0.14;this.line(x1,y,x2,y,'fine');[x1,x2].forEach(function(x){this.line(x-t,y-t,x+t,y+t,'med');this.line(x,y-0.45,x,y+0.45,'fine')},this);
  this.text((x1+x2)/2,up?y+0.18:y-0.55,label,{size:0.36,anchor:'middle'})};
/* ---------- catalogs ---------- */
var SYS={
  clad:{l:'Aluminum-clad wood',pre:'WD.',D:4.5625,finSet:1.0,fin:true,key:'clad'},
  wood:{l:'Wood',pre:'WD.',D:4.5625,finSet:1.0,fin:true,key:'wood'},
  aluminum:{l:'Aluminum',pre:'ALUM.',D:3.25,finSet:0.75,fin:true,key:'alum'},
  fiberglass:{l:'Fiberglass',pre:'FBRGL.',D:3.25,finSet:0.875,fin:true,key:'fg'},
  steel:{l:'Steel',pre:'STL.',D:1.75,finSet:0,fin:false,key:'steel'}};
var OPS={casement:{t:'PUSH OUT CASEMENT',min:3},awning:{t:'AWNING',min:3},fixed:{t:'FIXED',min:2.25},dh:{t:'DOUBLE HUNG',min:4},sh:{t:'SINGLE HUNG',min:4},slider:{t:'SLIDER',min:3.75}};
var STEEL_OPS=['casement','awning','fixed'];
var FIN_T={plaster:'CEMENT PLASTER',lap:'LAP SIDING',vertical:'VERTICAL WOOD SIDING',panel:'METAL PANEL'};
/* ---------- frame profiles: X = exterior face of frame, T = top of frame, D = jamb depth ---------- */
function igu(sh,x0,x1,yt,yb,thin){var l=thin?0.125:0.1875;sh.rect(x0,yb,l,yt-yb,'glass');sh.rect(x1-l,yb,l,yt-yb,'glass');sh.rect(x0+l,yt-0.5,x1-x0-2*l,0.375,'dark')}
function hollow(sh,x0,y0,x1,y1,t,c){sh.rect(x0,y0,x1-x0,y1-y0,c);if(x1-x0>2*t+0.05&&y1-y0>2*t+0.05)sh.rect(x0+t,y0+t,x1-x0-2*t,y1-y0-2*t,'void')}
function drawFrame(sh,s,X,T,D,yB){var sys=s.system,op=s.op,Xi=X-D,r={glass:null,bottom:T-2};
  if(sys==='clad'||sys==='wood'){var clad=sys==='clad';
    if(op==='fixed'){
      sh.rect(Xi,T-1.25,D,1.25,'woodf');sh.rect(X-0.75,T-2.25,0.75,1.0,'woodf');
      var g1=X-0.8125,g0=g1-0.875;igu(sh,g0,g1,T-1.375,yB);sh.rect(g0-0.625,T-2.0,0.625,0.75,'woodf');
      if(clad){sh.rect(X,T-2.25,0.0625,2.25,'clad');sh.rect(X-0.75,T-2.3125,0.8125,0.0625,'clad')}
      r.glass=[g0,g1];r.bottom=T-2.25;
    }else if(op==='dh'||op==='sh'){
      sh.rect(Xi,T-0.875,D,0.875,'woodf');sh.rect(X-0.75,T-1.625,0.75,0.75,'woodf');
      var u1=X-0.8125,u0=u1-1.375;sh.poly([[u0,T-1.0],[u1,T-1.0],[u1,T-3.0],[u0+0.3,T-3.0],[u0,T-2.7]],'woodf');
      igu(sh,u0+0.3,u1-0.3,T-2.5,yB,true);
      var p1=u0-0.0625,p0=p1-0.5;sh.rect(p0,T-1.5,0.5,0.625,'woodf');
      var l1=p0-0.0625,l0=l1-1.375;sh.line(l1,T-0.875,l1,yB,'beyond');sh.line(l0,T-0.875,l0,yB,'beyond');sh.line(l0+0.35,T-3.5,l0+0.35,yB,'beyond');
      if(l0-0.5>Xi+0.1)sh.rect(l0-0.5,T-1.625,0.5,0.75,'woodf');
      if(clad){sh.rect(X,T-1.625,0.0625,1.625,'clad');sh.rect(u1,T-3.0,0.0625,2.0,'clad')}
      r.glass=[u0+0.3,u1-0.3];r.bottom=T-3;
    }else if(op==='slider'){
      sh.rect(Xi,T-0.875,D,0.875,'woodf');
      var a1=X-0.5,a0=a1-1.375,b1=a0-0.3125,b0=b1-1.375;
      [[a0,a1],[b0,b1]].forEach(function(q){sh.rect(q[0],T-2.875,q[1]-q[0],1.875,'woodf');igu(sh,q[0]+0.3,q[1]-0.3,T-2.375,yB,true)});
      sh.rect(a1,T-1.375,0.5,0.5,'woodf');sh.rect(b1,T-1.25,0.3125,0.375,'woodf');
      if(clad){sh.rect(X,T-1.375,0.0625,1.375,'clad');sh.rect(a1-0.0625,T-2.875,0.0625,1.875,'clad')}
      r.glass=[a0+0.3,a1-0.3];r.bottom=T-2.875;
    }else{ // casement, awning
      sh.rect(Xi,T-1.125,D,1.125,'woodf');sh.rect(X-0.625,T-1.75,0.625,0.625,'woodf');
      var s1=X-0.6875,s0=s1-1.75;sh.poly([[s0,T-1.25],[s1,T-1.25],[s1,T-3.5],[s0+0.375,T-3.5],[s0,T-3.125]],'woodf');
      sh.rect(s0+0.25,T-1.25,0.375,0.125,'dark');
      var gc=(s0+s1)/2;igu(sh,gc-0.375,gc+0.375,T-3.0,yB);
      if(clad){sh.rect(X,T-1.75,0.0625,1.75,'clad');sh.rect(s1,T-3.5,0.0625,2.25,'clad')}
      r.glass=[gc-0.375,gc+0.375];r.bottom=T-3.5;
    }
    if(clad)sh.rect(X-SYS.clad.finSet,T,SYS.clad.finSet+0.0625,0.0625,'clad');
  }else if(sys==='aluminum'||sys==='fiberglass'){var c=sys==='aluminum'?'alum':'fgf',t=sys==='aluminum'?0.08:0.11;
    if(sys==='aluminum'&&D>2){var m=Xi+D*0.5;hollow(sh,Xi,T-1.5,m-0.18,T,t,c);sh.rect(m-0.18,T-1.1,0.36,0.6,'tbk');hollow(sh,m+0.18,T-1.5,X,T,t,c)}
    else hollow(sh,Xi,T-1.5,X,T,t,c);
    hollow(sh,X-0.5,T-2.25,X,T-1.5,t,c);
    if(op==='fixed'){var f1=X-0.6875,f0=f1-1.0;igu(sh,f0,f1,T-1.625,yB);sh.rect(f1,T-2.125,0.1875,0.375,'dark');hollow(sh,f0-0.625,T-2.25,f0-0.0625,T-1.5,t,c);sh.rect(f0-0.125,T-2.125,0.125,0.375,'dark');r.glass=[f0,f1];r.bottom=T-2.25}
    else if(op==='slider'){var q1=X-0.5625,q0=q1-1.25,w1=q0-0.25,w0=w1-1.25;[[q0,q1],[w0,w1]].forEach(function(q){hollow(sh,q[0],T-3.25,q[1],T-1.625,t,c);igu(sh,q[0]+0.25,q[1]-0.25,T-2.75,yB,true)});r.glass=[q0+0.25,q1-0.25];r.bottom=T-3.25}
    else{var k1=X-0.5625,k0=k1-Math.min(2,Math.max(1.4,D-1.1));hollow(sh,k0,T-3.5,k1,T-1.625,t,c);sh.rect(k0+0.2,T-1.625,0.3,0.125,'dark');var kc=(k0+k1)/2;igu(sh,kc-0.5,kc+0.5,T-3.0,yB);r.glass=[kc-0.5,kc+0.5];r.bottom=T-3.5}
  }else{ // steel: angle frame, tube sash, putty-glazed IGU
    sh.rect(Xi,T-0.1875,D,0.1875,'steelf');sh.rect(X-0.1875,T-1.25,0.1875,1.25,'steelf');
    if(op==='fixed'){var h1=X-0.3125,h0=h1-0.625;igu(sh,h0,h1,T-0.375,yB,true);sh.poly([[h0-0.0625,T-1.25],[h0-0.0625,T-0.1875],[h0-0.375,T-0.1875],[h0-0.375,T-0.375],[h0-0.25,T-0.375],[h0-0.25,T-1.25]],'steelf');r.glass=[h0,h1];r.bottom=T-1.25}
    else{var j1=X-0.25,j0=Math.max(Xi+0.125,j1-1.375);hollow(sh,j0,T-1.75,j1,T-0.3125,0.125,'steelf');var jc=(j0+j1)/2;igu(sh,jc-0.3125,jc+0.3125,T-1.5,yB,true);r.glass=[jc-0.3125,jc+0.3125];r.bottom=T-1.75}
  }
  return r}

/* ---------- the detail ---------- */
/* AlderCAD's batt symbol, ported as is from packages/bim-geometry/src/patterns.ts#battLoopsLocal:
   round loops alternating at the two faces, joined by crossing tangents so the line doubles back.
   Local frame: s along the cavity 0..length, y across it centered on 0. */
function battLoopsLocal(length,depth){
  if(!(length>0)||!(depth>0))return [];
  var r=depth*0.18,c=depth/2-r-depth*0.02,pitch=depth*0.36,step=pitch/2,n=Math.ceil(length/step)+2,circles=[];
  for(var i=-1;i<=n;i++){var top=((i%2)+2)%2===0;circles.push({c:[i*step,top?c:-c],ccw:!top})}
  var out=[],ARC=20,entry=null;
  for(var j=0;j+1<circles.length;j++){var A=circles[j],B=circles[j+1],dx=B.c[0]-A.c[0],dy=B.c[1]-A.c[1],L=Math.hypot(dx,dy);
    if(L<=2*r)return [];
    var delta=Math.atan2(dy,dx),beta=A.ccw?delta+Math.asin(2*r/L):delta-Math.asin(2*r/L),nx=-Math.sin(beta),ny=Math.cos(beta),sA=A.ccw?-1:1;
    var p1=[A.c[0]+sA*nx*r,A.c[1]+sA*ny*r],p2=[B.c[0]-sA*nx*r,B.c[1]-sA*ny*r],exit=Math.atan2(p1[1]-A.c[1],p1[0]-A.c[0]);
    if(entry!=null){var sw=exit-entry;if(A.ccw){while(sw<=0)sw+=2*Math.PI}else{while(sw>=0)sw-=2*Math.PI}
      for(var k=1;k<ARC;k++){var ang=entry+sw*k/ARC;out.push([A.c[0]+r*Math.cos(ang),A.c[1]+r*Math.sin(ang)])}}
    out.push(p1,p2);entry=Math.atan2(p2[1]-B.c[1],p2[0]-B.c[0])}
  return out}
/* batt in a vertical stud cavity from x0 to x1, y0 to y1, clipped to the cavity */
function batt(sh,x0,x1,y0,y1){var xc=(x0+x1)/2,pts=battLoopsLocal(y1-y0,x1-x0).map(function(q){return [xc+q[1],y0+q[0]]});
  if(pts.length<2)return;sh.clip([[x0,y0],[x1,y0],[x1,y1],[x0,y1]],function(k){k.pl(pts,'fine')})}
/* plywood in section, as the firm draws it: the plies as parallel lines, and a group of four
   short diagonals across the thickness every 4" */
function ply(sh,x0,y0,y1){var t=0.5;sh.rect(x0,y0,t,y1-y0,'ply');[0.25,0.5,0.75].forEach(function(f){sh.line(x0+t*f,y0,x0+t*f,y1,'fine')});
  for(var y=y0+1.5;y+0.9<y1;y+=4){for(var k=0;k<4;k++){var yy=y+k*0.17;sh.line(x0,yy+0.45,x0+t,yy,'fine')}}}
function zig(x0,x1,y){var m=(x0+x1)/2,z=0.6;return [[x0,y],[m-z,y],[m-z*0.35,y+z*1.4],[m+z*0.35,y-z*1.4],[m+z,y],[x1,y]]}
function breakH(sh,x0,x1,y){sh.pl(zig(x0,x1,y),'med')}
/* the drawing between a top and a bottom break line: everything outside is trimmed along the zigzags */
function breakRegion(top,bot){var B=1000;return [[-B,top.y]].concat(zig(top.x0,top.x1,top.y),[[B,top.y],[B,bot.y]],zig(bot.x0,bot.x1,bot.y).reverse(),[[-B,bot.y]])}

/* where the window sits in the wall: worked out once, so head, jamb and sill always agree */
function place(s){
  var warn=[],sys=SYS[s.system],p={warn:warn,sys:sys};
  var cmu=s.structure==='cmu';p.cmu=cmu;p.S1=cmu?7.625:(s.structure==='2x4'?3.5:5.5);p.sx=p.S1+(cmu?0:0.5);p.wrbT=0.6;
  /* the waterproofing layers, outward from the sheathing face, drawn at readable offsets.
     Shedding: a higher layer always lies OUTSIDE the lower one it overlaps.
     liner = membrane lining the rough opening (first on); wrb1/wrb2 = WRB on the wall below the
     head and at the jambs; pan = sill pan, over the WRB below; fin = nail fin, over the pan;
     tape = flashing over the fin at head and jambs; drip = drip flashing leg, over the tape;
     hw1/hw2 = the WRB above the head, lapped over the drip leg and the tape */
  p.L={liner:0.03,wrb1:0.06,wrb2:0.12,pan:0.2,fin:0.26,tape:0.38,drip:0.45,hw1:0.52,hw2:0.57};
  p.matT=s.ext==='panel'?0:0.25;p.W=p.sx+p.wrbT+p.matT;p.finT={plaster:0.875,lap:0.625,vertical:0.75,panel:2.31}[s.ext];p.F=p.W+p.finT;
  p.Xi=s.int==='wood'?-0.75:-0.625;p.retT={plaster:0.875,lap:0.75,vertical:0.75,panel:0.0625}[s.ext];
  var D=s.depth,mount=s.mount,trimP=s.trim==='brickmould'?1.25:0,Xwf,Xfin=null,sx=p.sx,F=p.F;
  p.noFin=s.kind==='door'&&!s.fin;
  if(mount==='flush'){if(p.noFin){Xfin=null;Xwf=p.W}else{Xfin=sx+p.L.fin;Xwf=Xfin+sys.finSet}}
  else if(mount==='buck'){Xwf=F-s.inset-trimP;Xfin=Xwf-sys.finSet;
    var maxFin=sx-0.75;if(Xfin>maxFin){var minIn=F-trimP-sys.finSet-maxFin;warn.push('A buck needs at least '+fmt(minIn)+' of inset here, so the window is drawn at '+fmt(minIn)+'. At less than that it can mount flush on the sheathing.');Xfin=maxFin;Xwf=Xfin+sys.finSet}
    if(Xfin<1.5){Xfin=1.5;Xwf=Xfin+sys.finSet;warn.push('That inset leaves less than 1-1/2" of buck to nail the fin to. Drawn at the deepest workable setting.')}}
  else{Xwf=F-s.inset;if(Xwf-D<p.Xi-2)warn.push('The frame reaches more than 2" past the interior face at this inset.')}
  p.D=D;p.mount=mount;p.trimP=trimP;p.Xwf=Xwf;p.Xfin=Xfin;p.Xfi=Xwf-D;
  if(p.Xfi<p.Xi-0.05)warn.push('The '+fmt(D)+' frame is deeper than this wall, so it stands '+fmt(p.Xi-p.Xfi)+' proud of the interior face. Check the product or the wall.');
  if(s.kind!=='door'&&s.system!=='steel'&&D<OPS[s.op].min-1e-6)warn.push('A '+OPS[s.op].t.toLowerCase()+' frame is rarely shallower than '+fmt(OPS[s.op].min)+'. Drawn as entered.');
  p.faceTrim=mount==='flush'&&(s.trim==='trim2x'||s.trim==='casing');
  p.trimT=s.trim==='trim2x'?1.5:1.0;p.trimH=s.trim==='trim2x'?5.5:3.5;p.trimBack=Math.max(p.W,Xwf);
  p.trimName=s.trim==='trim2x'?'2X WOOD TRIM':'5/4 EXT. CASING';
  p.inset=mount==='flush'?null:F-(Xwf+(mount==='buck'?trimP:0));
  p.drip=s.drip==='auto'?(s.ext==='panel'?'base':((p.faceTrim||(mount!=='block'&&s.trim==='brickmould'))?'copper':'metal')):s.drip;
  p.mlab=s.memb==='sa'?'SELF-ADHERED FLASHING':'FLUID-APPLIED FLASHING';
  p.wrbNote=s.ext==='plaster'?'2-LAYER W.R.B. W/ DRAINAGE MAT':(s.ext==='panel'?'W.R.B.':'W.R.B. W/ DRAINAGE MAT');
  p.finNote={plaster:'CEMENT PLASTER',lap:'FIBER CEMENT LAP SIDING',vertical:'VERTICAL WOOD SIDING',panel:'OPEN JOINT METAL PANEL'}[s.ext];
  return p}

/* ---------- head and jamb (the jamb is the head's plan, turned to line up with it) ---------- */
function buildHJ(s,p,view){
  var sh=new Sheet(),jamb=view==='jamb';
  var cmu=p.cmu,S1=p.S1,sx=p.sx,wrbT=p.wrbT,matT=p.matT,W=p.W,finT=p.finT,F=p.F,Xi=p.Xi,retT=p.retT;
  var hdrH=cmu?7.625:(jamb?3.0:9.25),yHB=cmu?-1.5:0,yTop=hdrH+(cmu?2.0:(jamb?5.0:2.75));
  var D=p.D,mount=p.mount,trimP=p.trimP,bh=jamb?1.5:3,Xwf=p.Xwf,Xfin=p.Xfin,yRO=mount==='buck'?yHB-bh:yHB;
  var T=yRO-0.5,Xfi=p.Xfi,yBot=T-4.5,ret=s.itrim==='return';

  /* interior finish */
  var gypBot=ret?yRO-0.5:T;
  sh.rect(Xi,gypBot,-Xi,yTop-gypBot,s.int==='wood'?'woodf':'gyp');
  if(ret){sh.rect(Xfi+0.0625,yRO-0.5,Xi-Xfi-0.0625,0.5,'gyp');sh.circ(Xi,yRO-0.5,0.09,'dot')}
  /* structure */
  if(cmu){
    sh.rect(0,0,1.25,yTop,'cmu');sh.rect(6.375,0,1.25,yTop,'cmu');sh.rect(1.25,0,5.125,yTop,'grout');
    if(!jamb){sh.circ(3.0,2.2,0.3125,'dot');sh.circ(4.6,2.2,0.3125,'dot')}
    sh.xbox(0,-1.5,7.625,1.5);
    sh.note(3.8,hdrH-2.0,jamb?'GROUTED CMU PER STRUCTURAL':'GROUTED BOND BEAM PER STRUCTURAL','left');sh.note(1.2,-0.75,'P.T. WOOD NAILER','left');
  }else{
    sh.line(0,hdrH,0,yTop,'med');sh.line(S1,hdrH,S1,yTop,'med');batt(sh,0,S1,hdrH,yTop);
    if(jamb){sh.xbox(0,0,S1,1.5,'woodcut');sh.xbox(0,1.5,S1,1.5,'woodcut');sh.note(S1*0.42,2.25,'KING AND TRIMMER STUDS','left')}
    else{sh.xbox(0,0,S1,hdrH,'woodcut');sh.note(S1*0.42,hdrH*0.55,'HEADER PER STRUCTURAL','left')}
    sh.line(0,hdrH,S1,hdrH,'cut');
    ply(sh,sx-0.5,0,yTop);
    sh.note(S1*0.45,yTop-2.0,'INSULATED WALL FRAMING','left');
    sh.note(sx-0.25,yTop-2.2,'PLYWOOD SHEATHING','right');
  }
  sh.note(Xi+0.3,yTop-2.6,s.int==='wood'?'WOOD PANELING PER INT. DESIGNER':'GYPSUM BOARD','left');

  /* buck */
  var yNotch=yHB;
  if(mount==='buck'){
    sh.xbox(0,yRO,Xfin,bh,'woodcut');
    sh.note(Xfin*0.35,yRO+bh*0.5,'SHAPED WOOD BUCK','left');
  }

  /* exterior finish: where it stops */
  var faceTrim=p.faceTrim,trimT=p.trimT,trimH=p.trimH,trimBack=p.trimBack,trimBot=T-0.75,trimTop=trimBot+trimH;
  var finBot;
  if(mount==='flush'){finBot=faceTrim?trimTop+(jamb?0.125:0.25):(s.ext==='plaster'?yHB-0.25:(s.ext==='panel'?T+0.375:T+0.5))}
  else finBot=s.ext==='plaster'?yHB-0.22-retT:yHB-0.22;  /* set deep: the return sits under the WRB that wraps the header */
  var drip=jamb?'none':p.drip;

  var L=p.L,two=s.ext==='plaster';
  if(jamb&&mount==='flush'){ /* at the jamb the WRB runs under the fin */
    sh.line(sx+L.wrb1,T,sx+L.wrb1,yTop,'wrb');if(two)sh.line(sx+L.wrb2,T,sx+L.wrb2,yTop,'wrb');
  }else if(mount==='flush'){ /* the WRB above laps down over the drip leg and the flashing over the fin */
    var wEnd=faceTrim?trimTop+0.4:T+0.4;
    sh.line(sx+L.hw1,wEnd,sx+L.hw1,yTop,'wrb');if(two)sh.line(sx+L.hw2,wEnd,sx+L.hw2,yTop,'wrb');
  }else{ /* set deep: the WRB wraps under the header and laps down over the flashing at the window */
    var rIn=mount==='buck'?Xfin+0.24:Xwf+0.375;
    [[L.hw1,0.12]].concat(two?[[L.hw2,0.17]]:[]).forEach(function(q,i){var x=sx+q[0],y=yHB-q[1],xi=rIn+i*0.05;
      sh.pl(mount==='buck'?[[x,yTop],[x,y],[xi,y],[xi,yHB-0.65]]:[[x,yTop],[x,y],[xi,y]],'wrb')})}
  if(matT)sh.line(W,finBot,W,yTop,'fine');

  if(s.ext==='plaster'){
    if(mount==='flush'){sh.rect(W,finBot,finT,yTop-finBot,'plaster');
      sh.pl([[W+0.04,finBot+0.6],[W+0.04,finBot-0.0625],[F+0.04,finBot-0.0625],[F+0.04,finBot+0.25]],'flash');sh.note(F-0.1,finBot-0.0625,'J-MOLD','right');
      if(jamb&&!faceTrim){sh.rect(W+0.3,T+0.02,F-W-0.3,finBot-0.1-T,'seal');sh.note(F-0.3,T+0.1,'SEALANT AND BACKER ROD','right')}}
    else{
      var rx=mount==='buck'?Xfin+0.3:Xwf+0.375;
      sh.poly([[W,yTop],[F,yTop],[F,finBot],[rx,finBot],[rx,finBot+retT],[W,finBot+retT]],'plaster');
      sh.pl([[F-0.35,finBot],[F,finBot],[F,finBot+0.35]],'flash');sh.note(F,finBot,'CORNER REINFORCING BEAD','right');}
    sh.note(F-0.3,yTop-2.6,'CEMENT PLASTER','right');
    sh.note(W-0.12,yTop-3.5,p.wrbNote,'right');
  }else if(s.ext==='lap'){
    sh.clip([[W-0.01,finBot],[F+1,finBot],[F+1,yTop],[W-0.01,yTop]],function(q){
      q.rect(W,finBot,0.25,0.25,'woodplain');
      for(var yb=finBot;yb<yTop;yb+=7){var top=yb+8.25;q.poly([[W,top],[W+0.3125,top],[W+0.625,yb],[W+0.3125,yb]],'woodplain')}});
    sh.note(W+0.4,finBot+4.5,p.finNote,'right');sh.note(W-0.12,yTop-2.4,p.wrbNote,'right');
  }else if(s.ext==='vertical'){
    sh.rect(W,finBot,0.75,yTop-finBot,'woodf');sh.note(F-0.2,yTop-2.0,p.finNote,'right');sh.note(W-0.12,yTop-3.2,p.wrbNote,'right');
  }else{
    sh.rect(sx+wrbT,finBot,1.5,yTop-finBot,'woodf');var pf=F-0.0625;
    sh.pl([[pf-0.6,finBot],[pf,finBot],[pf,yTop]],'flash');sh.pl([[pf-0.6,finBot+0.04],[pf-0.04,finBot+0.04],[pf-0.04,yTop]],'fine');
    sh.note(pf,yTop-2.0,p.finNote,'right');sh.note(sx+0.8,yTop-3.2,'P.T. SLEEPERS AND DRAINAGE CAVITY','right');sh.note(sx+0.03,yTop-4.4,p.wrbNote,'right');
  }
  if(mount!=='flush'&&s.ext!=='plaster'){
    var r0=mount==='buck'?Xfin+0.3:Xwf+0.375;
    if(s.ext==='panel'){sh.pl([[F,finBot+0.6],[F,yHB-0.22],[r0,yHB-0.22]],'flash');sh.note(r0+0.6,yHB-0.22,'SHEET METAL RETURN','right')}
    else{sh.rect(r0,finBot-retT,F-r0,retT,'woodf');sh.note(F-0.4,finBot-retT*0.5,'1X WOOD RETURN','right')}
  }

  /* membrane */
  if(mount==='flush'){
    sh.pl([[0.7,yRO-0.03],[sx+L.liner,yRO-0.03],[sx+L.liner,yRO+1.75]],'memb');   /* lines the opening and turns up the face; the flashing over the fin laps over its top */
    sh.pl(jamb?[[sx+L.tape,T+0.06],[sx+L.tape,T+1.6],[sx+L.wrb2+0.05,T+2.2],[sx+L.wrb2+0.05,T+4.5]]:[[sx+L.tape,T+0.06],[sx+L.tape,T+5.0]],'memb');  /* over the fin */
    sh.note(sx+L.tape,T+(jamb?3.4:4.2),p.mlab,'right')}
  else if(mount==='buck'){
    sh.pl([[0.7,yRO-0.03],[Xfin+0.02,yRO-0.03],[Xfin+0.02,Math.min(yRO+1.75,yHB-0.3)]],'memb');   /* lines the underside of the buck and turns up its face; the flashing over the fin laps over its top */
    /* over the fin, up the buck face, under the header and up the sheathing: one piece, under the WRB everywhere */
    sh.pl([[Xfin+0.16,T+0.06],[Xfin+0.16,yHB-0.03],[sx+L.liner,yHB-0.03],[sx+L.liner,yTop-4.8]],'memb');
    sh.note(sx+L.liner,yHB+1.6,p.mlab,'right')}
  else{sh.pl([[0.7,yRO-0.03],[sx+L.liner,yRO-0.03],[sx+L.liner,yTop-4.8]],'memb');sh.note(sx+L.liner,yRO+2.0,p.mlab,'right')}

  /* the jamb seen beyond: drawn before the pieces in front, from past the break up to the piece it belongs to */
  [ret?Xi:Xi-0.6875,Xfi,Xwf].forEach(function(x){sh.line(x,yBot,x,(ret&&x===Xi)?yRO-0.5:T,'beyond')});
  sh.line(F,yBot,F,Math.min(finBot,T),'beyond');

  /* window */
  var door=s.kind==='door',fr=(door?drawDoorHead:drawFrame)(sh,s,Xwf,T,D,yBot);
  if(Xfin!=null&&!p.noFin){sh.rect(mount==='buck'?Xfin+0.04:Xfin,T,0.06,1.25,'fin');sh.note(Xfin+0.03,T+0.9,'NAIL FIN','right')}
  else if(Xfin==null&&door){sh.circ(Xwf+0.19,T+0.3,0.14,'rod');sh.note(Xwf+0.19,T+0.3,'SEALANT AND BACKER ROD','right')}
  else if(Xfin==null){sh.circ(Xwf+0.19,finBot+retT*0.45,0.14,'rod');sh.rect(Xwf+0.02,finBot,0.12,retT||0.6,'seal');
    sh.note(Xwf+0.19,finBot+retT*0.45,'SEALANT AND BACKER ROD','right');sh.note(Xwf-D*0.5,T-0.1,'ANCHOR CLIPS PER MANUF.','right')}
  if(fr.glass)sh.note((fr.glass[0]+fr.glass[1])/2,yBot+2.2,door?fr.label:(mount==='block'?'WINDOW, FINLESS BLOCK FRAME':'WINDOW'),'right');

  /* exterior trim + head flashing */
  var bmTop=mount==='buck'?(s.ext==='plaster'?finBot:finBot-retT)-0.125:T;
  if(s.trim==='brickmould'&&mount!=='block'){var b0=Xwf+0.0625;sh.poly([[b0,bmTop],[b0+1.25,bmTop-0.2],[b0+1.25,T-1.4],[b0+1.0,T-1.65],[b0+0.6,T-1.65],[b0+0.6,T-2.0],[b0,T-2.0]],'woodf');sh.note(b0+1.2,T-1.3,'BRICKMOULD','right')}
  if(faceTrim){sh.xbox(trimBack,trimBot,trimT,trimH,'woodcut');
    sh.note(trimBack+trimT,trimBot+trimH*0.35,p.trimName,'right');
    if(trimBack>W+0.05)sh.rect(W,trimBot+0.75,trimBack-W,trimH-0.75,'woodplain');
    if(jamb){sh.rect(W+0.1,trimTop+0.02,Math.min(finT,trimBack+trimT-W)-0.1,0.1,'seal');sh.note(W+finT*0.6,trimTop+0.07,'SEALANT','right')}}
  if(drip==='copper'){var deepBm=mount==='buck'&&s.trim==='brickmould',top=faceTrim?trimTop:(deepBm?bmTop:T),front=faceTrim?trimBack+trimT:(s.trim==='brickmould'?Xwf+1.3125:Xwf+0.25),back=(faceTrim||mount==='flush')?sx+L.drip:(mount==='buck'?Xfin+0.2:Xwf+0.0625);
    sh.pl([[back,top+(faceTrim?2.6:(mount==='buck'?Math.max(0.3,yHB-0.3-top):(mount==='flush'?2.6:0.42)))],[back,top+0.08],[front+0.2,top-0.02],[front+0.2,top-0.4]],'copper');sh.note(front+0.2,top-0.2,'COPPER DRIP CAP','right');
    if(faceTrim&&s.ext!=='panel')sh.note(W+finT*0.6,trimTop+0.13,'1/4" GAP, DO NOT CAULK','right')}
  else if(drip==='base'){sh.pl([[sx+L.drip+0.03,finBot+1.6],[sx+L.drip+0.03,finBot-0.12],[F+0.25,finBot-0.2],[F+0.25,finBot-0.55]],'flash');sh.note(F+0.25,finBot-0.4,'METAL BASE TRIM W/ DRIP EDGE','right');
    if(mount==='flush'){sh.pl([[sx+L.drip,T+2.2],[sx+L.drip,T+0.1],[Xwf+0.3,T+0.02],[Xwf+0.3,T-0.32]],'flash');sh.note(Xwf+0.3,T-0.1,'SHEET METAL FLASHING','right')}}
  else if(drip==='metal'&&mount!=='block'){var mb=mount==='flush'?sx+L.drip:Xfin+0.2,mt=mount==='flush'?T+2.6:yHB-0.3;
    sh.pl([[mb,mt],[mb,T+0.1],[Xwf+trimP+0.3,T+0.02],[Xwf+trimP+0.3,T-0.32]],'flash');sh.note(Xwf+trimP+0.3,T-0.15,'SHEET METAL DRIP FLASHING','right')}

  /* interior */
  if(!ret){
    if(Xfi>Xi+0.05){sh.rect(Xi,T-0.6875,Xfi-Xi,0.6875,'woodf');sh.note(Xi+Math.min(0.8,(Xfi-Xi)/2),T-0.6875,'JAMB EXTENSION','left')}
    var cb=T-0.6875+0.1875,ct=cb+3.5,cx=Xi-0.6875;
    sh.rect(cx,cb,0.6875,3.5,'woodf');
    if(s.itrim==='backband')sh.poly([[Xi,ct],[Xi-1.0625,ct],[Xi-1.0625,ct-1.0625],[cx,ct-1.0625],[cx,ct]],'woodf');
    sh.note(cx,cb+2.6,'TRIM PER INT. DESIGNER','left');
    sh.note((Math.max(Xi,0)+(Xfin!=null?Math.min(Xfin,Xwf-0.5):Xfi))/2,(yRO+T)/2,'SHIM SPACE (FILL WITH EXPANDING FOAM SEALANT)','left');
  }else sh.note((Xi+Xfi)/2,yRO-0.25,'GYPSUM BOARD RETURN W/ CORNER BEAD','left');

  /* breaks: the drawing runs past them and is trimmed along the zigzag */
  var yTB=yTop-1.0,yBB=yBot+1.0,gl=fr.glass||[Xfi,Xwf];
  sh.wrapClip(breakRegion({x0:Xi-1.6,x1:F+1.2,y:yTB},{x0:gl[0]-0.9,x1:gl[1]+0.9,y:yBB}));
  breakH(sh,Xi-1.6,F+1.2,yTB);if(fr.glass)breakH(sh,gl[0]-0.9,gl[1]+0.9,yBB);
  sh.dimH(Xfi,Xwf,yBB+1.1,fmt(D)+' JAMB');
  if(mount!=='flush'){var outer=Xwf+(mount==='buck'?trimP:0);sh.dimH(outer,F,T-3.9,fmt(F-outer)+' INSET',false)}
  sh.layoutNotes(Xi-1.4,F+1.6);
  /* doors are drawn at the head only; the firm notes the jamb (Devon EXT13, Naini EXT05) */
  if(door)sh.text(Xi-1.4,yBB-2.3,'NOTE: JAMB CONDITION SIMILAR'+(drip!=='none'?' (OMIT DRIP CAP)':''),{size:0.375,anchor:'start'});
  return sh.svg();
}

/* ---------- sill frame profiles: X = exterior face of frame, yS = bottom of frame ---------- */
function drawSillFrame(sh,s,X,yS,D,yT,nose){var sys=s.system,op=s.op,Xi=X-D,r={glass:null};
  if(sys==='clad'||sys==='wood'){var clad=sys==='clad',Xs=X-Math.min(D*0.45,2.25),xn=X+nose,yn=yS+(nose>0?0.625:0.875);
    sh.poly([[Xi,yS],[xn,yS],[xn,yn],[Xs,yS+1.5],[Xi,yS+1.5]],'woodf');
    if(nose>0.6)sh.rect(xn-0.5,yS,0.125,0.125,'void');
    if(clad)sh.pl([[Xs,yS+1.56],[xn+0.05,yn+0.06],[xn+0.05,yS-0.06]],'cladln');
    if(op==='fixed'){var g1=X-0.8125,g0=g1-0.875;sh.rect(X-0.75,yS+1.25,0.75,0.875,'woodf');sh.rect(g0,yS+1.5,0.875,0.25,'dark');igu(sh,g0,g1,yT,yS+1.75);sh.rect(g0-0.625,yS+1.5,0.625,0.75,'woodf');r.glass=[g0,g1];
      if(clad)sh.rect(X,yS+1.25,0.0625,0.875,'clad')}
    else if(op==='dh'||op==='sh'){var u1=X-0.8125,u0=u1-1.375,p0=u0-0.5625,l1=p0-0.0625,l0=l1-1.375;
      sh.poly([[l0,yS+1.875],[l0+0.3,yS+1.625],[l1,yS+1.625],[l1,yS+4.125],[l0,yS+4.125]],'woodf');igu(sh,l0+0.3,l1-0.3,yT,yS+3.625,true);
      [u0,u1,u0+0.3,u1-0.3].forEach(function(x){sh.line(x,yS+6.5,x,yT,'beyond')});
      if(l0-0.5>Xi+0.1)sh.rect(l0-0.5,yS+1.5,0.5,0.75,'woodf');r.glass=[l0+0.3,l1-0.3]}
    else if(op==='slider'){var a1=X-0.5,a0=a1-1.375,b1=a0-0.3125,b0=b1-1.375;
      [[a0,a1],[b0,b1]].forEach(function(q){sh.rect(q[0],yS+1.75,q[1]-q[0],1.75,'woodf');igu(sh,q[0]+0.3,q[1]-0.3,yT,yS+3.0,true);sh.rect((q[0]+q[1])/2-0.0625,yS+1.5,0.125,0.25,'dark')});
      if(clad)sh.rect(a1,yS+1.75,0.0625,1.75,'clad');r.glass=[a0+0.3,a1-0.3]}
    else{var s1=X-0.6875,s0=s1-1.75;sh.poly([[s0,yS+3.875],[s1,yS+3.875],[s1,yS+1.625],[s0+0.375,yS+1.625],[s0,yS+2.0]],'woodf');
      sh.rect(s0+0.25,yS+1.5,0.375,0.125,'dark');var gc=(s0+s1)/2;igu(sh,gc-0.375,gc+0.375,yT,yS+3.375);
      if(clad)sh.rect(s1,yS+1.625,0.0625,2.25,'clad');r.glass=[gc-0.375,gc+0.375]}
  }else if(sys==='aluminum'||sys==='fiberglass'){var c=sys==='aluminum'?'alum':'fgf',t=sys==='aluminum'?0.08:0.11;
    if(sys==='aluminum'&&D>2){var m=Xi+D*0.5;hollow(sh,Xi,yS,m-0.18,yS+1.5,t,c);sh.rect(m-0.18,yS+0.45,0.36,0.6,'tbk');hollow(sh,m+0.18,yS,X,yS+1.5,t,c)}
    else hollow(sh,Xi,yS,X,yS+1.5,t,c);
    if(nose>0){sh.rect(X,yS+1.0,nose,0.08,c);sh.rect(X+nose-0.08,yS+0.5,0.08,0.58,c)}
    if(op==='fixed'){var f1=X-0.6875,f0=f1-1.0;sh.rect(f0,yS+1.5,1.0,0.25,'dark');igu(sh,f0,f1,yT,yS+1.75);hollow(sh,f0-0.625,yS+1.5,f0-0.0625,yS+2.25,t,c);r.glass=[f0,f1]}
    else if(op==='slider'){var q1=X-0.5625,q0=q1-1.25,w1=q0-0.25,w0=w1-1.25;[[q0,q1],[w0,w1]].forEach(function(q){hollow(sh,q[0],yS+1.625,q[1],yS+3.25,t,c);igu(sh,q[0]+0.25,q[1]-0.25,yT,yS+2.75,true)});r.glass=[q0+0.25,q1-0.25]}
    else{var k1=X-0.5625,k0=k1-Math.min(2,Math.max(1.4,D-1.1));hollow(sh,k0,yS+1.625,k1,yS+3.5,t,c);var kc=(k0+k1)/2;igu(sh,kc-0.5,kc+0.5,yT,yS+3.0);r.glass=[kc-0.5,kc+0.5]}
  }else{
    sh.rect(Xi,yS,D,0.1875,'steelf');sh.rect(X-0.1875,yS,0.1875,1.25,'steelf');
    if(op==='fixed'){var h1=X-0.3125,h0=h1-0.625;sh.rect(h0,yS+0.1875,0.625,0.1875,'dark');igu(sh,h0,h1,yT,yS+0.375,true);sh.rect(h0-0.375,yS+0.1875,0.3125,1.0,'steelf');r.glass=[h0,h1]}
    else{var j1=X-0.25,j0=Math.max(Xi+0.125,j1-1.375);hollow(sh,j0,yS+0.3125,j1,yS+1.75,0.125,'steelf');var jc=(j0+j1)/2;igu(sh,jc-0.3125,jc+0.3125,yT,yS+1.5,true);r.glass=[jc-0.3125,jc+0.3125]}
  }
  return r}

/* ---------- sill ---------- */
function buildSill(s,p){
  var sh=new Sheet(),cmu=p.cmu,S1=p.S1,sx=p.sx,wrbT=p.wrbT,W=p.W,finT=p.finT,F=p.F,Xi=p.Xi,retT=p.retT;
  var mount=p.mount,Xwf=p.Xwf,Xfin=p.Xfin,D=p.D,Xfi=p.Xfi,ret=s.itrim==='return',es=s.sill,L=p.L;
  var yR=cmu?1.5:0,bh=mount==='buck'?1.5:0,yB=yR+bh,yS=yB+0.5,yT=yS+5.5,yBot=yR-5.5;
  var faceTrim=p.faceTrim,trimT=p.trimT,trimH=p.trimH,trimBack=p.trimBack;

  /* structure */
  if(cmu){sh.rect(0,yBot,1.25,-yBot,'cmu');sh.rect(6.375,yBot,1.25,-yBot,'cmu');sh.rect(1.25,yBot,5.125,-yBot,'grout');sh.xbox(0,0,7.625,1.5);
    sh.note(3.8,yBot+2.5,'GROUTED CMU PER STRUCTURAL','left');sh.note(1.2,0.75,'P.T. WOOD NAILER','left')}
  else{sh.xbox(0,-1.5,S1,1.5,'woodcut');sh.xbox(0,-3,S1,1.5,'woodcut');sh.line(0,yBot,0,-3,'med');sh.line(S1,yBot,S1,-3,'med');batt(sh,0,S1,yBot,-3);
    ply(sh,sx-0.5,yBot,yR);
    sh.note(S1*0.45,-0.75,'DOUBLE 2X SILL','left');sh.note(S1*0.45,yBot+2.8,'INSULATED WALL FRAMING','left');sh.note(sx-0.25,yBot+2.2,'PLYWOOD SHEATHING','right')}
  /* interior finish */
  var stool=!ret&&s.isill==='stool',jTop=yS+1.5,jBot=jTop-0.6875;
  var gypTop=ret?yS:(stool?yS+0.5:jBot);
  sh.rect(Xi,yBot,-Xi,gypTop-yBot,s.int==='wood'?'woodf':'gyp');
  sh.note(Xi+0.3,yBot+3.2,s.int==='wood'?'WOOD PANELING PER INT. DESIGNER':'GYPSUM BOARD','left');
  /* buck */
  if(mount==='buck'){sh.xbox(0,yR,Xfin,bh,'woodcut');sh.note(Xfin*0.35,yR+bh*0.5,'SHAPED WOOD BUCK','left')}

  /* where the finish stops, and what sits on it */
  var nose=0,finTop,pieceBot=null;
  if(es==='window'){
    if(faceTrim){nose=Math.max(1.0,trimBack+trimT+0.375-Xwf);var tTop=yS-0.125;
      sh.xbox(trimBack,tTop-trimH,trimT,trimH,'woodcut');sh.note(trimBack+trimT,tTop-trimH*0.6,p.trimName,'right');
      if(trimBack>W+0.05)sh.rect(W,tTop-trimH,trimBack-W,trimH-0.5,'woodplain');
      sh.rect(trimBack+0.15,tTop,trimT-0.15,0.11,'seal');sh.note(trimBack+trimT*0.7,tTop+0.06,'SEALANT AND BACKER ROD','right');
      finTop=tTop-trimH-0.25}
    else{nose=Math.max(0.75,F+0.375-Xwf);finTop=yS-0.375}
  }else{ // a wood sill piece, alone or over a cut stone sill
    if(es==='woodpiece'||es==='stone'){
      var xb=Xwf,xf=(es==='stone'?F+0.25:(faceTrim?trimBack+trimT:F)+1.25),yPB=yS-0.625,ytb=yS+0.75,ytf=yPB+1.0;
      sh.poly([[xb,yPB],[xf,yPB],[xf,ytf],[xb,ytb]],'woodf');if(xf-xb>2)sh.rect(xf-0.6,yPB,0.1875,0.1875,'void');
      sh.note(xf-0.3,ytf-0.1,es==='stone'?'WOOD SILL PIECE':'WOOD SILL PIECE (EXTENDED)','right');pieceBot=yPB;
      if(es==='stone'){var deep=mount==='buck',sb=deep?Xfin+0.14:sx+0.32,xs=F+1.5,yst=yPB-0.0625,ysb=deep?Math.max(yR+0.0625,yst-1.75):yst-1.75,ysf=yst-0.35,mb=sx+0.32;
        sh.poly([[sb,ysb],[xs,ysb],[xs,ysf],[sb,yst]],'stone');sh.rect(xs-0.6,ysb,0.1875,0.1875,'void');
        sh.rect(mb,ysb-0.375,F-mb,0.375,'plaster');if(!deep)sh.poly([[mb,ysb-0.375],[mb+1.0,ysb-0.375],[mb,ysb+0.25]],'woodplain');
        sh.note(xs-0.4,ysf-0.4,'CUT STONE SILL WITH DRIP KERF SET IN MORTAR BED','right');if(!deep)sh.note(mb+0.3,ysb-0.25,'P.T. CANT STRIP','right');
        finTop=ysb-0.375}
      else if(faceTrim){var t2=yPB-0.0625;sh.xbox(trimBack,t2-trimH,trimT,trimH,'woodcut');sh.note(trimBack+trimT,t2-trimH*0.6,p.trimName,'right');
        if(trimBack>W+0.05)sh.rect(W,t2-trimH,trimBack-W,trimH-0.5,'woodplain');
        sh.circ(trimBack+trimT*0.6,t2+0.03,0.1,'rod');sh.note(trimBack+trimT*0.6,t2+0.03,'SEALANT','right');finTop=t2-trimH-0.25}
      else finTop=yPB-0.375;
    }else{ // sloped return on shaped blocking
      var r0=mount==='buck'?Xfin+0.3:Xwf+0.375,ya=yS+0.125,yf=yS-0.5,top=function(x){return ya+(yf-ya)*(x-r0)/(F-r0)};
      var bx0=mount==='buck'?Xfin:Xwf+0.375;
      sh.poly([[bx0,yB],[sx,yR],[sx,top(sx)-retT],[bx0,top(bx0)-retT]],'woodcut');sh.line(bx0,yB,sx,top(sx)-retT,'fine');
      /* the blocking is wrapped: membrane over its top, down the face over the pan */
      sh.pl([[bx0+0.02,top(bx0)-retT+0.04],[sx+L.pan+0.04,top(sx+L.pan)-retT+0.04],[sx+L.pan+0.04,yR-1.5]],'memb');
      sh.note((bx0+sx)/2,(yB+top(sx)-retT)/2,'CONT. SHAPED BLOCKING SLOPED AWAY FROM FRAME','right');
      if(s.ext==='panel'){sh.pl([[r0,top(r0)],[F+0.4,top(F)-0.05],[F+0.4,top(F)-0.6]],'flash');sh.note(F+0.4,top(F)-0.3,'SHEET METAL SILL FLASHING','right');finTop=top(F)-0.6}
      else{sh.poly([[r0,top(r0)],[F,top(F)],[F,top(F)-retT],[r0,top(r0)-retT]],s.ext==='plaster'?'plaster':'woodf');
        if(s.ext==='plaster'){sh.pl([[F-0.35,top(F)],[F,top(F)],[F,top(F)-0.35]],'flash');sh.note(F,top(F)-0.1,'CORNER REINFORCING BEAD','right')}
        else sh.note(F-0.3,top(F)-retT*0.5,'1X WOOD SILL RETURN','right');
        finTop=top(F)-retT}
      sh.circ(r0-0.19,yS+0.2,0.14,'rod');sh.note(r0-0.19,yS+0.2,'SEALANT AND BACKER ROD','right');
    }
  }

  /* exterior finish below */
  var wrbTop=yR-0.1;
  sh.line(sx+L.wrb1,yBot,sx+L.wrb1,wrbTop,'wrb');if(s.ext==='plaster')sh.line(sx+L.wrb2,yBot,sx+L.wrb2,wrbTop,'wrb');
  if(p.matT)sh.line(W,yBot,W,finTop,'fine');
  if(s.ext==='plaster'){sh.rect(W,yBot,finT,finTop-yBot,'plaster');
    if(!(es==='return')){sh.pl([[W+0.04,finTop-0.6],[W+0.04,finTop+0.0625],[F+0.04,finTop+0.0625],[F+0.04,finTop-0.25]],'flash');sh.note(F-0.1,finTop+0.0625,'J-MOLD','right');
      if(es==='window'&&!faceTrim){sh.circ(F-0.3,finTop+0.22,0.13,'rod');sh.note(F-0.3,finTop+0.22,'SEALANT AND BACKER ROD','right')}}}
  else if(s.ext==='lap'){var cTop=finTop;sh.clip([[W-0.01,yBot],[F+1,yBot],[F+1,cTop],[W-0.01,cTop]],function(q){for(var yb=cTop-7;yb>yBot-8;yb-=7){var tp=yb+8.25;q.poly([[W,tp],[W+0.3125,tp],[W+0.625,yb],[W+0.3125,yb]],'woodplain')}});sh.line(W,cTop,W+0.625,cTop,'med')}
  else if(s.ext==='vertical')sh.rect(W,yBot,0.75,finTop-yBot,'woodf');
  else{sh.rect(sx+wrbT,yBot,1.5,finTop-yBot,'woodf');var pf=F-0.0625;sh.pl([[pf,yBot],[pf,finTop],[pf-0.6,finTop]],'flash');
    if(es==='window')sh.pl([[sx+L.wrb2+0.03,finTop+0.25],[pf+0.25,finTop+0.12],[pf+0.25,finTop-0.3]],'flash')}
  sh.note(s.ext==='plaster'?F-0.3:(s.ext==='lap'?W+0.4:F-0.2),yBot+3.4,p.finNote,'right');sh.note(sx+0.03,yBot+2.3,p.wrbNote,'right');

  /* sill pan */
  /* the pan laps OVER the WRB below it; the fin goes over the pan, never taped at its bottom edge */
  var mx=0.5,path=mount==='buck'?[[mx,yB+0.75],[mx,yB+0.03],[Xfin+0.02,yB+0.03],[Xfin+0.02,yR+0.03],[sx+L.pan,yR+0.03],[sx+L.pan,yR-4.5]]:[[mx,yR+0.75],[mx,yR+0.03],[sx+L.pan,yR+0.03],[sx+L.pan,yR-4.5]];
  sh.pl(path,'memb');sh.note(sx+L.pan,yR-3.4,s.memb==='sa'?'PRE-FORMED SILL PAN':'FLUID-APPLIED FLASHING','right');

  /* the jamb seen beyond: drawn before the pieces in front, from the sill up past the break */
  [ret?Xi:Xi-0.6875,Xfi,Xwf,F].forEach(function(x){sh.line(x,yS,x,yT,'beyond')});

  /* window */
  var fr=drawSillFrame(sh,s,Xwf,yS,D,yT,nose);
  if(Xfin!=null){sh.rect(mount==='buck'?Xfin+0.04:Xfin,yS-1.25,0.06,1.25,'fin');sh.note(Xfin+0.03,yS-0.9,'NAIL FIN','right')}
  else sh.note(Xwf-D*0.5,yS+0.1,'ANCHOR CLIPS PER MANUF.','right');
  if(es==='window'&&nose>0)sh.note(Xwf+nose,yS+0.3,(s.system==='clad'||s.system==='wood')?'WINDOW SILL':'WINDOW SILL EXTENDER','right');
  if(fr.glass)sh.note((fr.glass[0]+fr.glass[1])/2,yT-1.4,mount==='block'?'WINDOW, FINLESS BLOCK FRAME':'WINDOW','right');

  /* interior */
  if(ret){sh.rect(Xi,yB,Xfi-Xi,yS-yB,'gyp');sh.circ(Xi,yS,0.09,'dot');sh.note((Xi+Math.min(Xfi,0))/2,yS-0.25,'GYPSUM BOARD RETURN W/ CORNER BEAD','left')}
  else if(stool){var sL=Xi-0.6875-1.1875;sh.rect(sL,yS+0.5,Xfi-sL,1.0,'woodf');sh.rect(Xi-0.6875,yS+0.5-3.5,0.6875,3.5,'woodf');
    sh.note(sL+0.4,yS+1.5,'SILL EXTENSION','left');sh.note(Xi-0.6875,yS-1.6,'INT. CASING','left')}
  else{if(Xfi>Xi+0.05){sh.rect(Xi,jBot,Xfi-Xi,0.6875,'woodf');sh.note(Xi+Math.min(0.8,(Xfi-Xi)/2),jTop,'JAMB EXTENSION','left')}
    var ct=jTop-0.1875;sh.rect(Xi-0.6875,ct-3.5,0.6875,3.5,'woodf');
    if(s.itrim==='backband')sh.poly([[Xi,ct-3.5],[Xi-1.0625,ct-3.5],[Xi-1.0625,ct-2.4375],[Xi-0.6875,ct-2.4375],[Xi-0.6875,ct-3.5]],'woodf');
    sh.note(Xi-0.6875,ct-1.0,'TRIM PER INT. DESIGNER','left')}
  if(!ret)sh.note((Math.max(Xi,0)+(Xfin!=null?Math.min(Xfin,Xwf-0.5):Xfi))/2,(yB+yS)/2,'SHIM SPACE (FILL WITH EXPANDING FOAM SEALANT)','left');

  /* breaks: the drawing runs past them and is trimmed along the zigzag */
  var yTB=yT-1.0,yBB=yBot+1.0,gl=fr.glass||[Xfi,Xwf];
  sh.wrapClip(breakRegion({x0:gl[0]-0.9,x1:gl[1]+0.9,y:yTB},{x0:Xi-1.6,x1:F+1.2,y:yBB}));
  breakH(sh,Xi-1.6,F+1.2,yBB);if(fr.glass)breakH(sh,gl[0]-0.9,gl[1]+0.9,yTB);
  sh.dimH(Xfi,Xwf,yTB-1.1,fmt(D)+' JAMB',true);
  if(mount!=='flush'){var outer=Xwf+(mount==='buck'?p.trimP:0);sh.dimH(outer,F,yS+5.0,fmt(F-outer)+' INSET',true)}
  sh.layoutNotes(Xi-1.4,F+1.6);
  return sh.svg();
}

/* ===================== DOORS (round 2, bim-plan/37) =====================
   Read from the issued sets (Devon EXT13/14, Naini EXT05, Hsu EXT06, Yuzefpolsky EXT05): a door gets a
   HEAD (the window head's wall, flashing and trim, with the door frame in place of the window's) and a
   THRESHOLD; the firm notes the jamb as similar to the head rather than drawing it. */
var DOPS={inswing:{t:'INSWING'},outswing:{t:'OUTSWING'},pivot:{t:'PIVOT'},slider:{t:'SLIDING'},multislide:{t:'MULTISLIDE'}};
var sliding=function(op){return op==='slider'||op==='multislide'};
/* where the leaves sit in the frame: X = exterior face of frame, D = frame depth. Head and threshold
   both read this, so a leaf can never sit differently at the top and the bottom. */
function doorLeaves(s,X,D){var Xi=X-D,lt=s.system==='aluminum'?1.5:1.75;
  if(sliding(s.op)){var n=s.op==='multislide'?Math.max(2,Math.min(4,s.lanes||3)):2,pitch=(D-0.5)/n,out=[];
    for(var i=0;i<n;i++){var c=X-0.25-pitch*(i+0.5),t=Math.min(lt,pitch-0.25);out.push({x0:c-t/2,x1:c+t/2})}
    return {leaves:out,stop:null}}
  if(s.op==='outswing'){var o0=Xi+1.25;return {leaves:[{x0:o0,x1:o0+lt}],stop:{x0:Xi,x1:Xi+1.25},inSide:true}}
  if(s.op==='pivot'){var pc=(X+Xi)/2;return {leaves:[{x0:pc-lt/2,x1:pc+lt/2}],stop:null}}
  var i1=X-1.25;return {leaves:[{x0:i1-lt,x1:i1}],stop:{x0:i1,x1:X},inSide:false}}
var DMAT=function(s){return s.system==='aluminum'?'alum':s.system==='fiberglass'?'fgf':s.system==='steel'?'steelf':'woodf'};
var hollowSys=function(s){return s.system==='aluminum'||s.system==='fiberglass'||s.system==='steel'};
var railOf=function(s,top){return sliding(s.op)?3.5:(s.glazed?(top?4.5:8):0)};
function leafBody(sh,s,x0,x1,y0,y1){var c=DMAT(s);if(hollowSys(s))hollow(sh,x0,y0,x1,y1,s.system==='steel'?0.125:0.1,c);else sh.rect(x0,y0,x1-x0,y1-y0,c);
  if(s.system==='clad'&&!sliding(s.op))sh.rect(x1,y0,0.0625,y1-y0,'clad')}
/* one leaf, from its top (yTop) down past the break (yBot): a rail, then glass, or the solid panel */
function drawLeafDown(sh,s,x0,x1,yTop,yBot){var rail=railOf(s,true);
  if(!rail){leafBody(sh,s,x0,x1,yBot,yTop);return}
  leafBody(sh,s,x0,x1,yTop-rail,yTop);var g0=x0+(x1-x0)*0.3,g1=x1-(x1-x0)*0.3;
  sh.rect(g0,yTop-rail-0.375,g1-g0,0.375,'dark');igu(sh,g0,g1,yTop-rail-0.375,yBot,true)}
/* one leaf, from its bottom (yB) up past the break (yTop) */
function drawLeafUp(sh,s,x0,x1,yB,yTop){var rail=railOf(s,false);
  if(!rail){leafBody(sh,s,x0,x1,yB,yTop);return}
  leafBody(sh,s,x0,x1,yB,yB+rail);var g0=x0+(x1-x0)*0.3,g1=x1-(x1-x0)*0.3;
  sh.rect(g0,yB+rail,g1-g0,0.375,'dark');igu(sh,g0,g1,yTop,yB+rail+0.375,true)}
/* the door's head frame: X = exterior face of frame, T = top of frame */
function drawDoorHead(sh,s,X,T,D,yB){var Xi=X-D,c=DMAT(s),sl=sliding(s.op),hj=sl?1.25:1.0625,L=doorLeaves(s,X,D);
  if(hollowSys(s))hollow(sh,Xi,T-hj,X,T,s.system==='steel'?0.125:0.1,c);else sh.rect(Xi,T-hj,D,hj,c);
  if(s.system==='clad')sh.rect(X,T-hj,0.0625,hj,'clad');
  var yLeaf=T-hj-0.125;
  if(L.stop){sh.rect(L.stop.x0,T-hj-0.5,L.stop.x1-L.stop.x0,0.5,c);
    var ws=L.inSide?L.stop.x1:L.stop.x0-0.12;sh.rect(ws,T-hj-0.42,0.12,0.3,'dark');sh.note(ws+0.06,T-hj-0.3,'WEATHER STRIPPING',L.inSide?'left':'right')}
  if(s.op==='pivot'){var pl=L.leaves[0];sh.rect(pl.x0+0.2,yLeaf,pl.x1-pl.x0-0.4,0.12,'dark');sh.note((pl.x0+pl.x1)/2,yLeaf+0.05,'TOP PIVOT PER MANUF.','left')}
  if(sl)L.leaves.forEach(function(l){var cx=(l.x0+l.x1)/2;sh.rect(cx-0.15,T-hj-0.7,0.3,0.7,'dark')});
  L.leaves.forEach(function(l){drawLeafDown(sh,s,l.x0,l.x1,sl?T-hj-0.1875:yLeaf,yB)});
  var g0=Math.min.apply(null,L.leaves.map(function(l){return l.x0})),g1=Math.max.apply(null,L.leaves.map(function(l){return l.x1}));
  return {glass:[g0,g1],bottom:T-hj,label:sl?'SLIDING DOOR PANELS':(s.glazed?'DOOR (GLAZED)':'DOOR')}}

/* a vertical dimension (the step at the threshold) */
Sheet.prototype.dimV=function(x,y1,y2,label){var t=0.14;this.line(x,y1,x,y2,'fine');[y1,y2].forEach(function(y){this.line(x-t,y-t,x+t,y+t,'med');this.line(x-0.45,y,x+0.45,y,'fine')},this);
  this.text(x+0.3,(y1+y2)/2-0.15,label,{size:0.36,anchor:'start'})};

/* ---------- threshold ---------- */
function buildThreshold(s,p){
  var sh=new Sheet(),Xi=p.Xi,F=p.F,sx=p.sx,Xwf=p.Xwf,D=p.D,Xfi=p.Xfi,L=doorLeaves(s,Xwf,D),sl=sliding(s.op);
  var framed=s.floor==='framed',fl=0.75,step=framed?1.75:0.75;
  var xL=Xi-1.25,xR=F+1.0,yTop=9.5,yBot=framed?-7.5:-5.5;
  var xa=Xfi-0.25,xn=Xwf+(sl?0.75:1.125),xE=framed?sx:Math.max(Xwf+0.75,xn-0.375);
  /* the floor */
  if(framed){var jd=11.25,sub=0.75;
    sh.rect(xL,-sub,sx-0.5-xL,sub,'woodplain');sh.xbox(sx-2.0,-sub-jd,1.5,jd,'woodcut');
    sh.line(xL,-sub-jd,sx-2.0,-sub-jd,'beyond');ply(sh,sx-0.5,-sub-jd-1,-0.02);
    sh.note(sx-1.25,-sub-2.5,'RIM JOIST PER STRUCTURAL','left');sh.note(xL+0.6,-sub*0.5,'3/4" PLYWOOD SUBFLOOR','left');sh.note(xL+0.6,-sub-3.5,'FLOOR FRAMING PER STRUCTURAL','left');
    /* the deck outside: boards over sleepers, below the threshold by the step */
    /* the deck outside: its ledger bolted through the sheathing to the rim, boards on top, below the
       threshold by the step */
    var dk=-step,dx0=sx+0.25;sh.rect(dx0,dk-1.0,xR-dx0,1.0,'woodf');sh.xbox(dx0,dk-1.0-7.25,1.5,7.25,'woodcut');
    sh.line(sx+0.06,-sub-jd-1,sx+0.06,-0.4,'wrb');
    /* the pan wraps the subfloor edge and laps down over the WRB on the rim (it sheds) */
    sh.pl([[xa-0.1,0.04],[sx+0.12,0.04],[sx+0.12,-4.5]],'memb');sh.note(sx+0.12,-2.2,'FLUID-APPLIED FLASHING','right');
    sh.note(xR-0.4,dk-0.5,'DECK PER PLAN','right');sh.note(dx0+0.75,dk-4.5,'LEDGER PER STRUCTURAL','right');
    sh.dimV(xR-0.3,0.08,dk,'PER PLAN')}
  else{
    sh.poly([[xL,0],[xE,0],[xE,yBot-2],[xL,yBot-2]],'conc');
    var xj=xE+0.5;sh.poly([[xj,-step],[xR,-step-0.15],[xR,yBot-2],[xj,yBot-2]],'conc');
    sh.rect(xE,yBot-2,0.5,-yBot+2-step-1.0,'joint');sh.circ(xE+0.25,-step-0.65,0.24,'dot');sh.rect(xE+0.02,-step-0.45,0.46,0.4,'seal');
    sh.pl([[xa-0.1,0.04],[xE+0.04,0.04],[xE+0.04,-1.4]],'flash');
    [xa+0.6,(xa+xn)/2+0.3].forEach(function(x){sh.line(x-0.2,0.1,x+0.2,0.1,'memb')});
    sh.note(xL+0.6,-2.5,'CONCRETE SLAB PER STRUCTURAL','left');sh.note((xa+xE)/2-0.5,0.04,'SHEET METAL SILL FLASHING','left');sh.note(xa+0.6,0.12,"SEALANT PER MANUF. REQ'S.",'left');
    sh.note(xR-0.3,-step-0.1,'PAVING PER SITE PLAN','right');sh.note(xE+0.25,-step-0.65,'SEALANT AND BACKER ROD','right');sh.note(xE+0.25,-3.6,'EXPANSION JOINT FILLER','right');
    sh.dimV(xE+0.25,0,-step,'3/4"')}
  /* the finished floor inside */
  sh.line(xL,fl,xa-0.15,fl,'floorln');sh.note(xL+0.6,fl,'FLOORING PER FINISH SCHEDULE','left');
  /* the jamb seen beyond, from the threshold up past the break */
  [Xi-0.6875,Xi,Xfi,Xwf].forEach(function(x){sh.line(x,1.0,x,yTop+1,'beyond')});sh.line(p.F,framed?0.2:1.0,p.F,yTop+1,'beyond');
  sh.note(Xi-0.3,yTop-3.2,'CASING BEYOND','left');
  /* the threshold: an aluminum extrusion sloping to the outside */
  var c='alum';
  if(sl){sh.rect(xa,0.08,xn-1.0-xa,0.55,c);sh.poly([[xn-1.0,0.63],[xn,0.4],[xn,0.08],[xn-1.0,0.08]],c);
    L.leaves.forEach(function(l){var cx=(l.x0+l.x1)/2;sh.rect(cx-0.1,0.63,0.2,0.4,c);sh.circ(cx,1.15,0.14,'dot');drawLeafUp(sh,s,l.x0,l.x1,1.3,yTop+1)});
    sh.note(xn-0.3,0.5,'THRESHOLD / SILL TRACK','right')}
  else{var lf=L.leaves[0];
    if(s.op==='outswing'){sh.rect(xa,0.08,lf.x0-0.1-xa,1.5,'woodf');sh.note(xa+0.4,1.5,'WOOD SILL STOP','left');
      sh.poly([[lf.x0-0.1,0.08],[xn,0.08],[xn,0.35],[lf.x0-0.1,1.05]],c);sh.rect(lf.x0+0.3,1.05,0.12,0.25,'dark')}
    else{sh.poly([[xa,0.08],[xn,0.08],[xn,0.35],[lf.x1+0.2,0.95],[xa+0.4,1.0],[xa,0.7]],c);sh.rect(lf.x1-0.05,0.97,0.3,0.28,'dark')}
    drawLeafUp(sh,s,lf.x0,lf.x1,1.25,yTop+1);
    var sw=s.op==='outswing'?lf.x0-0.12:lf.x1;sh.rect(sw,1.25,0.12,0.35,'dark');sh.note(sw+0.06,1.4,'DOOR SWEEP',s.op==='outswing'?'left':'right');
    sh.note(xn-0.4,0.4,'THRESHOLD','right')}
  var g0=Math.min.apply(null,L.leaves.map(function(l){return l.x0})),g1=Math.max.apply(null,L.leaves.map(function(l){return l.x1}));
  sh.note((g0+g1)/2,yTop-3.2,sl?'SLIDING DOOR PANELS':'DOOR ASSEMBLY','right');
  sh.labels={left:'INTERIOR',right:'EXTERIOR'};
  /* breaks: across the door above, across the floor below */
  var yTB=yTop,yBB=yBot+1.0,lx0=Math.min(Xi-1.2,g0-0.9),lx1=Math.max(F+1.0,g1+0.9);
  sh.wrapClip(breakRegion({x0:lx0,x1:lx1,y:yTB},{x0:xL,x1:xR,y:yBB}));
  breakH(sh,lx0,lx1,yTB);breakH(sh,xL,xR,yBB);
  sh.layoutNotes(xL-0.4,xR+0.4);
  return sh.svg()}

/* ---------- the engine's entry points ---------- */
var PARTS = { head: 'HEAD', jamb: 'JAMB', sill: 'SILL' }
/** the detail's title in the firm's naming pattern, e.g. "WD. WINDOW HEAD - INSET PUSH OUT CASEMENT AT CEMENT PLASTER" */
export function windowDetailTitle(s, part) {
  var sys = SYS[s.system] || SYS.clad
  return sys.pre + ' WINDOW ' + PARTS[part] + ' - ' + (s.mount === 'flush' ? '' : 'INSET ') + OPS[s.op].t + ' AT ' + (s.structure === 'cmu' ? 'CMU WALL' : FIN_T[s.ext])
}
/** draw one part: AnnoPrims in feet, their bbox in feet, and any warnings the placement raised */
export function drawWindowDetail(s, part) {
  var p = place(s)
  var r = part === 'sill' ? buildSill(s, p) : buildHJ(s, p, part === 'jamb' ? 'jamb' : 'head')
  return { prims: r.prims, bboxFt: r.bboxFt, warnings: p.warn.slice() }
}
var DPARTS = { head: 'HEAD', threshold: 'THRESHOLD' }
/** e.g. "WD. DOOR HEAD - INSWING AT CEMENT PLASTER", "ALUM. MULTISLIDE PATIO DOOR THRESHOLD" */
export function doorDetailTitle(s, part) {
  var sys = SYS[s.system] || SYS.clad, sl = sliding(s.op), inset = s.mount !== 'flush'
  var what = s.op === 'multislide' ? 'MULTISLIDE PATIO DOOR ' : s.op === 'slider' ? 'SLIDING PATIO DOOR ' : 'DOOR '
  var how = sl ? (inset ? ' - INSET' : '') : ' - ' + (inset ? 'INSET ' : '') + DOPS[s.op].t
  var at = part === 'threshold' ? (s.floor === 'framed' ? ' AT WOOD FLOOR' : '') : ' AT ' + (s.structure === 'cmu' ? 'CMU WALL' : FIN_T[s.ext])
  return sys.pre + ' ' + what + DPARTS[part] + how + at
}
/** draw one door part (head or threshold): AnnoPrims in feet, bbox in feet, warnings */
export function drawDoorDetail(s, part) {
  var p = place(s)
  var r = part === 'threshold' ? buildThreshold(s, p) : buildHJ(s, p, 'head')
  return { prims: r.prims, bboxFt: r.bboxFt, warnings: p.warn.slice() }
}
export const WINDOW_DETAIL_SYSTEMS = SYS
export const WINDOW_DETAIL_OPS = OPS
